import React, {useEffect, useRef, useState} from 'react';

import {Matcher} from 'react-day-picker';

import bem from 'client/services/bem';
import {parseDate} from 'client/services/utils/date';

import {isDateRange} from 'client/common/inputs/datetime/datepicker-calendar';
import DatepickerCalendar from 'client/common/inputs/datetime/datepicker-calendar/datepicker-calendar';
import Popover from 'client/common/popovers/popover';
import {PopoverRef} from 'client/common/popovers/popover/types';

import {Translation} from 'client/models/language/types';

import {DateInput} from '../datepicker-input';

import cssModule from './datepicker-base.module.scss';

const b = bem('datepicker-base', {cssModule});

export type DatepickerBaseProps = {
  value?: string;
  onChange?: (value: string) => void;
  disabledDates?: Matcher | Matcher[];
  placeholder?: Translation;
  name?: string;
  disabled?: boolean;
  className?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  status?: 'error' | 'warning';
  outputFormat?: 'ISO' | 'ISO_DATE' | 'DATE';
  timezone?: string;
  calendarFooter?: React.ReactNode;
  calendarClassNames?: {
    root: string;
  };
};

export const DatepickerBase = React.forwardRef<HTMLInputElement, DatepickerBaseProps>((props, ref) => {
  const {
    value,
    onChange,
    disabledDates,
    name,
    disabled,
    onBlur,
    placeholder,
    status,
    outputFormat = 'DATE',
    timezone,
    calendarFooter,
    calendarClassNames,
  } = props;
  const refPopover = useRef<PopoverRef>(null);
  const [selected, setSelected] = useState(value);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const handleChangeDay = (date?: string) => {
    onChange?.(date || '');
    refPopover.current?.setOpen(false);
  };

  const handleInputChange = (date?: string) => {
    setSelected(parseDate(date, {outputFormat, timezone}) || '');
  };

  const handleInputBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
    onChange?.(parseDate(selected, {outputFormat, timezone}) || '');
    onBlur?.(e);
  };

  return (
    <Popover
      ref={refPopover}
      trigger="click"
      role="dialog"
      disableFocus
      triggerClassName={b('trigger')}
      overlay={
        <div>
          <DatepickerCalendar
            {...props}
            timezone={timezone}
            outputFormat={outputFormat}
            mode="single"
            disabled={disabledDates}
            value={selected}
            onChange={handleChangeDay}
            classNames={calendarClassNames}
          />
          {calendarFooter}
        </div>
      }
      disabled={disabled}
    >
      <div className={b('input-container')}>
        <DateInput
          className={b('input')}
          name={name}
          ref={ref}
          status={status}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          disabled={disabled}
          value={isDateRange(selected) ? selected?.from : (selected as string)}
          placeholder={placeholder}
        />
      </div>
    </Popover>
  );
});
